import React, { useEffect, useState, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import "../../App.css";
import StartExamContent from "../../components/PlayContent/Exam/StartExamContent";
import {
  getExamProgress,
  getExamQuestions,
  updateExamProgress,
} from "../../endpoints/Assessment/functions";
import { Button, Card, CardActions, CardContent } from "@mui/material";

const StartExamPage = (props) => {
  const navigate = useNavigate();
  const { examName } = useParams();
  const [loading, setLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);
  const location = useLocation();
  const examId = location.state?.examId || null;
  const [allQuestions, setAllQuestions] = useState([]);
  const [finalScore, setFinalScore] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);

  const fetchExamData = useCallback(
    async (examSelected) => {
      try {
        const response =
          props.token && examId
            ? await getExamProgress(examId, props.token)
            : await getExamQuestions(examSelected);

        if (!response?.questions) return;

        const parsedResponse = response.questions.map((q) => ({
          ...q,
          choices: shuffle(q.choices),
        }));

        setAllQuestions(parsedResponse);

        const progressData = response.exam?.progress_data || {};
        const entries = Object.values(progressData);

        if (entries.length > 0 && !response.completed) {
          const maxIndex = Math.max(...entries.map((item) => item.index));
          setCurrentQuestion(maxIndex + 1);
        }
      } catch (error) {
        console.warn("Error at getAllQuestionsByExam", error);
      }
    },
    [props.token, examId],
  );

  async function updateProgress(progress) {
    const examId = location.state?.examId;
    if (examId) {
      try {
        let response = await updateExamProgress(examId, props.token, progress);
        if (response?.completed) {
          setFinalScore(response?.exam?.last_grade);
        }
      } catch (error) {
        console.warn("Progress was not saved", error);
      }
    } else {
      const updatedScore = progress.is_correct ? 1 : 0;
      setFinalScore(finalScore + updatedScore);
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await fetchExamData(examName);
      } catch (error) {
        console.log("Error at StartExamPage mount", error);
      } finally {
        setLoading(false);
        setHasFetched(true); // Confirms the API cycle has completely finished
      }
    };
    fetchData();
  }, [examName, fetchExamData]);

  function shuffle(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }

  return (
    <>
      {/* Strict Condition: Only shows if loading is done AND the API officially finished with 0 results */}
      {hasFetched && !loading && allQuestions.length === 0 ? (
        <div id="playing-mode-alert-exam" className="page">
          <Card sx={{ boxShadow: "var(--box-shadow)" }}>
            <div className="header">
              <h1>No Questions Found</h1>
            </div>
            <CardContent>
              Looks like you do not have any questions for this selected exam.
              Click below to return back home.
            </CardContent>
            <CardActions>
              <Button
                onClick={() => navigate(-1)}
                disableElevation
                color="black"
                variant="contained"
                className="py-1"
                sx={{ width: "8rem" }}
              >
                Go Home
              </Button>
            </CardActions>
          </Card>
        </div>
      ) : (
        <StartExamContent
          loading={loading}
          allQuestions={allQuestions}
          currentQuestion={currentQuestion}
          setCurrentQuestion={setCurrentQuestion}
          count={allQuestions.length}
          finalScore={finalScore}
          setFinalScore={setFinalScore}
          updateProgress={updateProgress}
          token={props.token}
          examId={examId}
          examName={examName}
        />
      )}
    </>
  );
};

export default StartExamPage;
